import { z } from 'zod';
import { MILLISECONDS_PER_DAY } from './dates.js';
import { DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE, DocumentDiscountTypeSchema, isDocumentDiscountWithinBounds, } from './document-discount.js';
import { ImageVariantUrlsSchema } from './images.js';
import { LineItemDiscountTypeSchema, LineItemUnitTypeSchema } from './line-items.js';
/** Minimum interval between reminder emails for the same invoice. */
export const INVOICE_REMINDER_RATE_LIMIT_MS = MILLISECONDS_PER_DAY;
/**
 * Rejects calendar-invalid dates (e.g. `2024-02-30`) that still pass the `yyyy-MM-dd`
 * format check. Guards against `Invalid Date` before calling `toISOString`, which throws
 * a raw `RangeError` instead of a validation issue.
 */
function isValidCalendarDate(val) {
    const parsed = new Date(`${val}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(val);
}
/** Line item variant that references an existing catalogue entry by ID. */
export const RegistryLinkedInvoiceLineItemSchema = z
    .object({
    lineItemId: z.string().min(1),
    quantity: z.number().positive(),
    discount: z.number().min(0).optional(),
    discountType: LineItemDiscountTypeSchema.optional(),
})
    .strict()
    .refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Line item variant with all fields specified inline — no catalogue reference. */
export const AdHocInvoiceLineItemSchema = z
    .object({
    title: z.string().min(1, { error: 'Title is required' }),
    description: z.string().optional(),
    unitPrice: z
        .number({ error: 'A valid unit price is required' })
        .min(0, { error: 'Unit price must be ≥ 0' }),
    quantity: z.number().positive({ error: 'Quantity must be positive' }),
    unitType: LineItemUnitTypeSchema,
    discount: z.number().min(0).optional(),
    discountType: LineItemDiscountTypeSchema.optional(),
    taxable: z.boolean().optional(),
})
    .refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Union of registry-linked and ad-hoc line item creation shapes. */
export const CreateInvoiceLineItemSchema = z.union([
    RegistryLinkedInvoiceLineItemSchema,
    AdHocInvoiceLineItemSchema,
]);
/** Request body for attaching a catalogue line item to an invoice. */
export const AttachInvoiceLineItemSchema = z.object({
    lineItemId: z.string().min(1),
});
/** Request body for updating an existing invoice line item. All fields are optional. */
export const UpdateInvoiceLineItemSchema = z
    .object({
    title: z
        .string()
        .min(1, { error: 'Title must contain at least 1 character' })
        .max(255)
        .optional(),
    description: z.union([z.string(), z.null()]).optional(),
    unitPrice: z.coerce
        .number()
        .min(0, { error: 'Unit price must be 0 or greater' })
        .optional(),
    unitType: LineItemUnitTypeSchema.optional(),
    quantity: z.coerce
        .number()
        .positive({ error: 'Quantity must be a positive number' })
        .optional(),
    discount: z.coerce.number().min(0).optional(),
    discountType: LineItemDiscountTypeSchema.optional(),
    taxable: z.boolean().optional(),
})
    .refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Single item in a batch update request — `id` present means update, absent means create. */
export const BatchInvoiceLineItemSchema = z
    .object({
    id: z.string().optional(),
    lineItemId: z.string().optional(),
    title: z.string().min(1, { error: 'Title is required' }),
    description: z.string().optional(),
    unitPrice: z.coerce.number().min(0, { error: 'Unit price must be ≥ 0' }),
    unitType: LineItemUnitTypeSchema,
    quantity: z.coerce.number().positive({ error: 'Quantity must be positive' }),
    discount: z.coerce.number().min(0).default(0),
    discountType: LineItemDiscountTypeSchema.default('NONE'),
    taxable: z.boolean().default(true),
})
    .refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Request body for batch-updating all line items on an invoice. */
export const BatchUpdateInvoiceLineItemsSchema = z.object({
    lineItems: z.array(BatchInvoiceLineItemSchema),
});
/** The possible lifecycle statuses for an invoice. */
export const InvoiceStatusSchema = z.enum([
    'DRAFT',
    'SENT',
    'PAID',
    'OVERDUE',
    'CANCELLED',
    'VOID',
]);
/** Tax type applied to the invoice total. */
export const InvoiceTaxTypeSchema = z.enum([
    'NONE',
    'GST',
    'VAT',
    'HST',
    'PST',
    'SALES_TAX',
    'CUSTOM',
]);
/**
 * Tax types selectable on AU-only revenue documents — invoices, quotes, recurring
 * invoices, and the organisation's default tax type. Excludes the non-Australian types
 * from {@link InvoiceTaxTypeSchema} (VAT, HST, PST, SALES_TAX): myTrellis only issues
 * Australian-domiciled invoices/quotes, so a business never charges those. Expenses keep
 * the full {@link InvoiceTaxTypeSchema}, since they can record genuine foreign tax paid
 * to an overseas vendor.
 */
export const DocumentTaxTypeSchema = z.enum(['NONE', 'GST', 'CUSTOM']);
const InvoiceFieldsSchema = z.object({
    clientId: z.string().min(1, { error: 'You must select a client from the list' }),
    invoiceNumber: z
        .string()
        .trim()
        .min(1, { error: 'Invoice number cannot be empty' })
        .max(50, { error: 'Invoice number must be 50 characters or fewer' })
        .optional(),
    locale: z.literal('en-AU').optional(),
    currency: z.literal('AUD').optional(),
    taxType: DocumentTaxTypeSchema.default('NONE'),
    taxRate: z.coerce
        .number()
        .min(0, { error: 'Tax rate cannot be negative' })
        .max(1, { error: 'Tax rate cannot exceed 100%' })
        .default(0),
    discount: z.coerce.number().min(0, { error: 'Discount cannot be negative' }).default(0),
    discountType: DocumentDiscountTypeSchema.default('NONE'),
    notes: z
        .string()
        .transform((val) => (val === '' ? null : val))
        .pipe(z.string().nullable())
        .default(null),
    terms: z
        .string()
        .nullable()
        .transform((val) => (val === '' ? null : val))
        .default(null),
    issueDate: z.iso
        .date({ error: 'Issue date is required' })
        .refine((val) => isValidCalendarDate(val), {
        error: 'Issue date must be a valid calendar date',
    })
        .transform((val) => `${val}T00:00:00.000Z`),
    dueAt: z.iso
        .date({ error: 'Due date is required' })
        .refine((val) => isValidCalendarDate(val), {
        error: 'Due date must be a valid calendar date',
    })
        .transform((val) => `${val}T00:00:00.000Z`),
    remindersEnabled: z.boolean().optional(),
    /**
     * The picked reminder schedule; null or omitted uses the client's own, else the
     * default.
     */
    reminderScheduleId: z.string().min(1).nullable().optional(),
    lineItems: z.array(CreateInvoiceLineItemSchema).optional(),
});
/** Request body for creating a new invoice. */
export const CreateInvoiceSchema = InvoiceFieldsSchema.refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Partial version of {@link CreateInvoiceSchema} for PATCH updates. */
export const UpdateInvoiceSchema = InvoiceFieldsSchema.partial().refine(isDocumentDiscountWithinBounds, DOCUMENT_DISCOUNT_PERCENTAGE_BOUND_ISSUE);
/** Request body for duplicating an invoice, optionally overriding its invoice number. */
export const DuplicateInvoiceSchema = z.object({
    invoiceNumber: z
        .string()
        .trim()
        .min(1, { error: 'Invoice number cannot be empty' })
        .max(50, { error: 'Invoice number must be 50 characters or fewer' })
        .optional(),
});
/** Request body for recording a manual (non-Stripe) payment against an invoice. */
export const RecordPaymentSchema = z.object({
    amount: z.number().positive({ error: 'Amount must be positive' }),
    currency: z.literal('AUD').optional(),
    paidAt: z.iso.datetime({ error: 'Paid date is required' }),
    note: z
        .string()
        .transform((val) => (val === '' ? null : val))
        .pipe(z.string().nullable())
        .default(null),
});
/**
 * Response for uploading a payment receipt — signed URLs plus the stored file's metadata.
 * Mirrors {@link ExpenseReceiptResponseSchema}.
 */
export const PaymentReceiptResponseSchema = z.object({
    receiptUrls: ImageVariantUrlsSchema,
    receiptFilename: z.string(),
    receiptMimeType: z.string(),
    receiptSizeBytes: z.number(),
});
/** Signed URL map for viewing a stored payment receipt. */
export const PaymentReceiptUrlsResponseSchema = z.object({
    receiptUrls: ImageVariantUrlsSchema,
});
//# sourceMappingURL=invoices.js.map